import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined'
import { Box, ButtonBase, Chip, Paper, Stack, Typography } from '@mui/material'
import { useState } from 'react'

const INDIGO = '#4338ca'

// Presentational, portal-agnostic "Integrations" hub. Callers pass a list of integration tiles;
// selecting a tile renders that integration's own settings panel below the grid. Built for N
// integrations — today it ships with a single tile (Google Business) in both the support and
// mentor portals, but new integrations only need another entry in the `integrations` array.
//
// integrations: Array<{
//   id: string,                       // stable key
//   title: string,
//   description?: string,             // one-line tile subtitle
//   Icon?: React.ComponentType,       // MUI icon component (not an element)
//   statusChip?: { label, color },    // optional status pill on the tile
//   renderPanel: () => React.ReactNode // the settings UI shown when the tile is selected
// }>
export default function IntegrationsHub({ integrations = [], title, subtitle, sx = {} }) {
  const available = integrations.filter(Boolean)
  const [selectedId, setSelectedId] = useState(available[0]?.id)
  const selected = available.find((item) => item.id === selectedId) || available[0]

  if (!available.length) {
    return (
      <Box sx={{ p: 3, ...sx }}>
        <Typography color="text.secondary">No integrations are available yet.</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, ...sx }}>
      {(title || subtitle) && (
        <Box sx={{ mb: 2 }}>
          {title && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExtensionOutlinedIcon sx={{ color: INDIGO }} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.15rem' }}>{title}</Typography>
            </Box>
          )}
          {subtitle && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {available.map((item) => {
          const active = item.id === selected?.id
          const TileIcon = item.Icon || ExtensionOutlinedIcon
          return (
            <ButtonBase
              key={item.id}
              onClick={() => setSelectedId(item.id)}
              focusRipple
              sx={{
                display: 'block',
                textAlign: 'left',
                borderRadius: '14px',
                width: '100%',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.75,
                  height: '100%',
                  borderRadius: '14px',
                  border: '1.5px solid',
                  borderColor: active ? INDIGO : '#e5e7eb',
                  bgcolor: active ? 'rgba(67,56,202,0.05)' : 'background.paper',
                  transition: 'border-color 120ms ease, background-color 120ms ease',
                  '&:hover': { borderColor: active ? INDIGO : '#c7cbd1' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <TileIcon sx={{ color: INDIGO, fontSize: 22 }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{item.title}</Typography>
                  {item.statusChip && (
                    <Chip
                      label={item.statusChip.label}
                      size="small"
                      color={item.statusChip.color || 'default'}
                      sx={{ ml: 'auto', height: 20, fontSize: 11, fontWeight: 700 }}
                    />
                  )}
                </Box>
                {item.description && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
                    {item.description}
                  </Typography>
                )}
              </Paper>
            </ButtonBase>
          )
        })}
      </Box>

      {selected && (
        <Paper
          elevation={0}
          sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: '14px', border: '1px solid #e5e7eb' }}
        >
          <Stack spacing={0}>{selected.renderPanel?.()}</Stack>
        </Paper>
      )}
    </Box>
  )
}
